// Block-level command primitives. Each function takes the editor's
// root element + the live `Selection` (browser global) and mutates
// the DOM to transform the block(s) containing the current caret /
// selection. They return nothing — the caller queries the new state
// via `deriveActiveFormats` and re-emits the markdown string.
//
// Block model: every direct child of the root is one of:
//   - <p>           paragraph (default)
//   - <blockquote>  quote
//   - <pre>         fenced code block (wraps <code>)
//   - <ul>/<ol>     bullet / numbered list (children: <li>)
//
// Lists may contain nested <ul>/<ol> inside any <li> to express
// nested levels. The markdown serializer renders nested levels as
// 2-space indentation per depth.

/**
 * Walk up from `node` to the nearest direct child of `root`. Returns
 * `null` when `node` isn't a descendant of `root`. This is "the block
 * the selection is in" — paragraph, blockquote, ul/ol, pre.
 */
export function getBlockContaining(
  root: HTMLElement,
  node: Node | null,
): HTMLElement | null {
  if (node === null) return null;
  // Selection anchored directly ON the root (common on an empty editor
  // and in Safari after programmatic edits): resolve to the root's
  // first block so toolbar block-toggles don't silently no-op.
  if (node === root) {
    return (root.firstElementChild as HTMLElement | null) ?? null;
  }
  let cur: Node | null = node;
  while (cur !== null && cur.parentNode !== null && cur.parentNode !== root) {
    cur = cur.parentNode;
  }
  if (cur === null || cur.parentNode !== root) return null;
  if (cur.nodeType !== Node.ELEMENT_NODE) return null;
  return cur as HTMLElement;
}

/**
 * Walk up from `node` to the nearest enclosing `<li>` element within
 * `root`. Returns `null` when no `<li>` ancestor exists.
 */
export function getListItemContaining(
  root: HTMLElement,
  node: Node | null,
): HTMLLIElement | null {
  let cur: Node | null = node;
  while (cur !== null && cur !== root) {
    if (
      cur.nodeType === Node.ELEMENT_NODE &&
      (cur as HTMLElement).tagName === 'LI'
    ) {
      return cur as HTMLLIElement;
    }
    cur = cur.parentNode;
  }
  return null;
}

/**
 * Replace `block` with a freshly built block of `newTag`, moving the
 * children across. Caret-anchored — after the swap the selection
 * sits inside the new block at the same character offset.
 */
function replaceBlockTag(
  block: HTMLElement,
  newTag: string,
): HTMLElement {
  const doc = block.ownerDocument;
  const fresh = doc.createElement(newTag);
  while (block.firstChild !== null) fresh.appendChild(block.firstChild);
  block.replaceWith(fresh);
  return fresh;
}

/**
 * Toggle the block type containing the selection. When the block is
 * already the requested type → revert to a plain `<p>`. Otherwise →
 * replace with the requested tag.
 */
export function toggleBlockType(
  root: HTMLElement,
  selection: Selection | null,
  targetTag: 'BLOCKQUOTE' | 'P',
): void {
  if (selection === null || selection.rangeCount === 0) return;
  const anchor = selection.anchorNode;
  const block = getBlockContaining(root, anchor);
  if (block === null) return;
  if (block.tagName === targetTag) {
    replaceBlockTag(block, 'p');
    return;
  }
  replaceBlockTag(block, targetTag.toLowerCase());
}

/**
 * Toggle a list at the block(s) intersecting the selection.
 *
 * Behavior:
 *   - Selection entirely inside a list of the same kind → strip the
 *     list (convert each `<li>` back to `<p>`).
 *   - Selection inside a list of the opposite kind → swap UL ↔ OL.
 *   - Selection covers multiple paragraphs → wrap them as siblings
 *     inside a single list element with one `<li>` per paragraph.
 *   - Single paragraph (or unrecognized block) → wrap in a one-item
 *     list.
 */
export function toggleListBlock(
  root: HTMLElement,
  selection: Selection | null,
  listTag: 'UL' | 'OL',
): void {
  if (selection === null || selection.rangeCount === 0) return;
  const blocks = getBlocksIntersectingSelection(root, selection);
  if (blocks.length === 0) return;

  // Detect "everything is already the requested list" / "everything
  // is the opposite list" so we can choose strip / swap. We only
  // consider the *top-level* list element since nested lists keep
  // their kind during top-level toggles.
  const allMatch = blocks.every((b) => b.tagName === listTag);
  const allOppositeList = blocks.every(
    (b) => b.tagName === oppositeListTag(listTag),
  );

  if (allMatch) {
    // Strip: expand every <li> back to a <p>.
    for (const list of blocks) {
      unwrapList(list);
    }
    return;
  }

  if (allOppositeList) {
    for (const list of blocks) swapListTag(list, listTag);
    return;
  }

  // Mixed / paragraph case: build a single new list and migrate each
  // selected block into it as an <li>.
  const doc = root.ownerDocument;
  const newList = doc.createElement(listTag);
  let firstBlock = blocks[0];
  // If the first block is a paragraph and the next sibling list is
  // already the requested kind, we could merge — but to keep things
  // predictable we always create a fresh list and insert it where the
  // first block sat.
  if (firstBlock === undefined) return;
  firstBlock.parentNode?.insertBefore(newList, firstBlock);
  for (const block of blocks) {
    if (block.tagName === 'UL' || block.tagName === 'OL') {
      // Pull the existing items into the new list.
      const items = Array.from(block.children).filter(
        (n) => n.tagName === 'LI',
      ) as HTMLLIElement[];
      for (const li of items) newList.appendChild(li);
      block.parentNode?.removeChild(block);
      continue;
    }
    const li = doc.createElement('li');
    while (block.firstChild !== null) li.appendChild(block.firstChild);
    if (li.childNodes.length === 0) li.appendChild(doc.createElement('br'));
    newList.appendChild(li);
    block.parentNode?.removeChild(block);
  }
}

function oppositeListTag(tag: 'UL' | 'OL'): 'UL' | 'OL' {
  return tag === 'UL' ? 'OL' : 'UL';
}

function swapListTag(list: HTMLElement, newTag: 'UL' | 'OL'): void {
  const doc = list.ownerDocument;
  const fresh = doc.createElement(newTag);
  while (list.firstChild !== null) fresh.appendChild(list.firstChild);
  list.replaceWith(fresh);
}

/**
 * Replace `list` with a sequence of `<p>` blocks — one per `<li>`.
 * Nested lists inside each `<li>` are flattened to their own list
 * blocks after the parent's paragraph so content survives the strip.
 */
function unwrapList(list: HTMLElement): void {
  const doc = list.ownerDocument;
  const parent = list.parentNode;
  if (parent === null) return;
  const items = Array.from(list.children).filter(
    (n) => n.tagName === 'LI',
  ) as HTMLLIElement[];
  for (const li of items) {
    // Extract nested lists so they end up as their own blocks.
    const nested = Array.from(li.children).filter(
      (n) => n.tagName === 'UL' || n.tagName === 'OL',
    ) as HTMLElement[];
    for (const n of nested) n.parentNode?.removeChild(n);
    const p = doc.createElement('p');
    while (li.firstChild !== null) p.appendChild(li.firstChild);
    if (p.childNodes.length === 0) p.appendChild(doc.createElement('br'));
    parent.insertBefore(p, list);
    for (const n of nested) parent.insertBefore(n, list);
  }
  parent.removeChild(list);
}

/**
 * Compute the set of top-level blocks (direct children of `root`)
 * that the live selection intersects. For range selections that span
 * several paragraphs this returns every block in between; for caret-
 * only selections it returns the single block under the caret.
 */
export function getBlocksIntersectingSelection(
  root: HTMLElement,
  selection: Selection,
): HTMLElement[] {
  if (selection.rangeCount === 0) return [];
  const range = selection.getRangeAt(0);
  const startBlock = getBlockContaining(root, range.startContainer);
  const endBlock = getBlockContaining(root, range.endContainer);
  if (startBlock === null) return [];
  if (endBlock === null || endBlock === startBlock) return [startBlock];
  // Collect every direct child of root between startBlock and endBlock
  // inclusive.
  const out: HTMLElement[] = [];
  let cur: Element | null = startBlock;
  while (cur !== null) {
    out.push(cur as HTMLElement);
    if (cur === endBlock) break;
    cur = cur.nextElementSibling;
  }
  return out;
}

/**
 * Detect a `` ``` `` (or longer) at the start of the paragraph the
 * caret sits in and convert that paragraph to a fenced code block.
 * Any text on the same line after the backticks moves into the
 * code body; the caret lands inside the `<pre><code>` block.
 * Returns `true` when a conversion happened.
 *
 * Slack-style trigger: this is invoked from the Enter handler so
 * the user types `` ``` `` and then Enter — matching how Slack /
 * GitHub / Discord do it.
 */
export function tryConvertParagraphToCodeBlock(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (selection === null || selection.rangeCount === 0 || !selection.isCollapsed) {
    return false;
  }
  const block = getBlockContaining(root, selection.anchorNode);
  if (block === null) return false;
  if (block.tagName !== 'P' && block.tagName !== 'DIV') return false;
  const doc = block.ownerDocument;
  const range = selection.getRangeAt(0);

  const line = caretLineInfo(block, range);
  if (line === null) return false;
  const match = line.text.trim().match(/^(`{3,})(.*)$/);
  if (match === null) return false;
  // Opening fence length (CommonMark). A block opened with 4+ backticks only
  // closes on a fence at least that long — which is what lets the user type a
  // NESTED code block (an inner ``` ``` ```) inside it without the inner fence
  // closing the outer block. Recorded as `data-fence` (omitted for the default
  // 3 so plain blocks stay attribute-free).
  const fenceLen = (match[1] ?? '```').length;

  // A single bare token right after the fence is the language label
  // (```js, ```python). Anything with whitespace is treated as body.
  const remainder = (match[2] ?? '').trim();
  let language = '';
  let body = '';
  if (/^[A-Za-z0-9+#.\-]{1,24}$/.test(remainder)) {
    language = remainder.toLowerCase();
  } else {
    body = remainder;
  }

  // Build the block. The caret target differs by whether the block starts
  // empty: Chrome silently DROPS a range placed inside an empty `<code>` that
  // holds only an empty text node (rangeCount falls to 0 → the next keystroke
  // goes nowhere, so the block looks broken). A `<br>` placeholder gives the
  // caret a valid position at `(code, 0)`; the first typed character replaces
  // it. With a non-empty body we anchor at the end of the real text node.
  const pre = doc.createElement('pre');
  if (language.length > 0) pre.setAttribute('data-lang', language);
  if (fenceLen > 3) pre.setAttribute('data-fence', String(fenceLen));
  const code = doc.createElement('code');
  let caretNode: Node;
  let caretOffset: number;
  if (body.length > 0) {
    const codeText = doc.createTextNode(body);
    code.appendChild(codeText);
    caretNode = codeText;
    caretOffset = body.length;
  } else {
    code.appendChild(doc.createElement('br'));
    caretNode = code;
    caretOffset = 0;
  }
  pre.appendChild(code);

  // Extract the content BEFORE and AFTER the fence line as fragments (so any
  // bold/italic/link on the surrounding lines is preserved), dropping the one
  // line-break delimiter that separated each from the fence line. Works
  // whether the soft breaks are `\n` characters (typed under `white-space:
  // pre-wrap`) or `<br>` elements (from a parsed markdown draft).
  const hasBefore = line.start > 0;
  const hasAfter = line.end < line.total;
  // Extract the AFTER region first (higher offsets) so extracting BEFORE
  // afterwards doesn't shift positions we've already resolved.
  const afterFrag = hasAfter
    ? extractOffsetRange(block, line.end + 1, line.total)
    : null;
  const beforeFrag = hasBefore
    ? extractOffsetRange(block, 0, line.start - 1)
    : null;

  if (beforeFrag !== null) {
    while (block.firstChild !== null) block.removeChild(block.firstChild);
    block.appendChild(beforeFrag);
    block.after(pre);
  } else {
    block.replaceWith(pre);
  }
  if (afterFrag !== null) {
    const afterP = doc.createElement('p');
    afterP.appendChild(afterFrag);
    if (afterP.childNodes.length === 0) afterP.appendChild(doc.createElement('br'));
    pre.after(afterP);
  }

  const r = doc.createRange();
  r.setStart(caretNode, caretOffset);
  r.collapse(true);
  const win = doc.defaultView;
  if (win !== null) {
    const sel = win.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
  }
  return true;
}

/**
 * The caret's current line inside `block`, expressed as CHARACTER offsets into
 * the block's text — where every soft break (a `\n` character OR a `<br>`
 * element) counts as one character. Both break styles occur in practice: a
 * `<br>` from parsed markdown vs. a literal `\n` typed under `white-space:
 * pre-wrap` (which is how the editor renders, so Chrome inserts `\n` on
 * Shift+Enter rather than a `<br>`). Handling both is what makes the fence
 * trigger fire on the second/third line of a multi-line paragraph.
 *
 * Returns `{ text, start, end, total }`: the line's text, its start/end
 * offsets, and the block's total length. `null` if the caret isn't resolvable.
 */
interface CaretLineInfo {
  readonly text: string;
  readonly start: number;
  readonly end: number;
  readonly total: number;
}

function caretLineInfo(block: HTMLElement, range: Range): CaretLineInfo | null {
  const before = block.ownerDocument.createRange();
  before.selectNodeContents(block);
  try {
    before.setEnd(range.startContainer, range.startOffset);
  } catch {
    return null;
  }
  const beforeText = textWithBreaks(before.cloneContents());
  const fullText = textWithBreaks(block);
  if (beforeText.length > fullText.length) return null;
  const afterText = fullText.slice(beforeText.length);

  const start = beforeText.lastIndexOf('\n') + 1;
  const nlAfter = afterText.indexOf('\n');
  const lineAfter = nlAfter === -1 ? afterText : afterText.slice(0, nlAfter);
  const text = beforeText.slice(start) + lineAfter;
  return { text, start, end: start + text.length, total: fullText.length };
}

/**
 * Concatenated text of `node`'s content with every `<br>` rendered as `\n`, so
 * a DOM subtree's string form matches the character model `caretLineInfo` and
 * `offsetToDomPosition` share. Text nodes contribute their raw value.
 */
function textWithBreaks(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'BR') {
    return '\n';
  }
  let out = '';
  for (const child of Array.from(node.childNodes)) out += textWithBreaks(child);
  return out;
}

/**
 * Resolve a character offset (same `<br>`-as-one-char model as `textWithBreaks`)
 * to a concrete DOM position `{ node, offset }` inside `block`, suitable for
 * `Range.setStart` / `setEnd`. Clamps to the end of the block when `target`
 * runs past the content.
 */
function offsetToDomPosition(
  block: HTMLElement,
  target: number,
): { node: Node; offset: number } {
  let remaining = target;
  let result: { node: Node; offset: number } | null = null;
  const visit = (node: Node): boolean => {
    if (result !== null) return true;
    if (node.nodeType === Node.TEXT_NODE) {
      const len = (node.textContent ?? '').length;
      if (remaining <= len) {
        result = { node, offset: remaining };
        return true;
      }
      remaining -= len;
      return false;
    }
    if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'BR') {
      if (remaining === 0) {
        const parent = node.parentNode;
        if (parent !== null) {
          result = { node: parent, offset: indexOfChild(parent, node) };
          return true;
        }
      }
      remaining -= 1;
      return false;
    }
    for (const child of Array.from(node.childNodes)) {
      if (visit(child)) return true;
    }
    return false;
  };
  for (const child of Array.from(block.childNodes)) {
    if (visit(child)) break;
  }
  return result ?? { node: block, offset: block.childNodes.length };
}

function indexOfChild(parent: Node, child: Node): number {
  let i = 0;
  let cur = parent.firstChild;
  while (cur !== null) {
    if (cur === child) return i;
    i++;
    cur = cur.nextSibling;
  }
  return i;
}

/** Extract `[start, end)` (character offsets) from `block` as a fragment. */
function extractOffsetRange(
  block: HTMLElement,
  start: number,
  end: number,
): DocumentFragment {
  const doc = block.ownerDocument;
  const r = doc.createRange();
  const a = offsetToDomPosition(block, start);
  const b = offsetToDomPosition(block, end);
  r.setStart(a.node, a.offset);
  r.setEnd(b.node, b.offset);
  return r.extractContents();
}

/**
 * Input-rule trigger: the user just typed a backtick and the caret's current
 * line is now exactly a bare ``` fence (three or more backticks, nothing
 * else). Convert it to an empty code block IMMEDIATELY — matching Slack /
 * Notion / Discord, which open the block on the third backtick with no Enter.
 * This is the primary, discoverable way to start a code block; the Enter /
 * Shift+Enter paths remain as fallbacks. Returns `true` when it converted.
 */
export function tryOpenCodeBlockOnType(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (
    selection === null ||
    selection.rangeCount === 0 ||
    !selection.isCollapsed
  ) {
    return false;
  }
  const block = getBlockContaining(root, selection.anchorNode);
  if (block === null) return false;
  if (block.tagName !== 'P' && block.tagName !== 'DIV') return false;
  const line = caretLineInfo(block, selection.getRangeAt(0));
  if (line === null) return false;
  // Only fire on a line that is EXACTLY the fence (no language, no body) so we
  // never swallow prose that merely contains backticks, and so the user is
  // dropped into an empty block ready to type.
  if (!/^`{3,}$/.test(line.text.trim())) return false;
  return tryConvertParagraphToCodeBlock(root, selection);
}

/**
 * Fence escalation. The Slack quick-open (`tryOpenCodeBlockOnType`) fires on
 * the 3rd backtick, so a user can't type a 4-backtick opening fence directly.
 * When they type ANOTHER backtick immediately after — while the freshly-opened
 * block still holds nothing but a run of backticks — treat it as extending the
 * opening fence: bump the block's `data-fence` and reset the code to empty.
 * That lets the user reach a 4+ backtick block (which only closes on a fence
 * that long), the prerequisite for authoring a NESTED code block. The caller
 * gates this to the just-opened window so it can't fire on arbitrary
 * backtick-only content mid-document. Returns `true` when it escalated.
 */
export function tryEscalateCodeFenceOnType(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (
    selection === null ||
    selection.rangeCount === 0 ||
    !selection.isCollapsed
  ) {
    return false;
  }
  const block = getBlockContaining(root, selection.anchorNode);
  if (block === null || block.tagName !== 'PRE') return false;
  const code = block.querySelector('code');
  if (code === null) return false;
  const text = textWithBreaks(code).replace(/\n$/, '');
  if (!/^`+$/.test(text)) return false; // not a pure fence run — leave as content
  const current = Math.max(
    3,
    Number.parseInt(block.getAttribute('data-fence') ?? '3', 10) || 3,
  );
  block.setAttribute('data-fence', String(current + text.length));
  // Reset the code to its empty caret placeholder.
  const doc = block.ownerDocument;
  while (code.firstChild !== null) code.removeChild(code.firstChild);
  code.appendChild(doc.createElement('br'));
  const r = doc.createRange();
  r.setStart(code, 0);
  r.collapse(true);
  const sel = doc.defaultView?.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(r);
  return true;
}

/**
 * Closing-fence exit: when the caret is inside a code block and the
 * current line is exactly ``` (the closing fence the user typed), drop
 * that fence line and move the caret to a fresh paragraph AFTER the
 * block — matching the "type ```, content, ```" mental model and giving
 * the block a keyboard exit (plain Enter otherwise just inserts a
 * newline inside the fence forever). Returns `true` when it handled the
 * Enter; `false` (bail) on anything unexpected so the caller falls back
 * to a normal newline.
 */
export function tryExitCodeFence(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (selection === null || selection.rangeCount === 0 || !selection.isCollapsed) {
    return false;
  }
  const block = getBlockContaining(root, selection.anchorNode);
  if (block === null || block.tagName !== 'PRE') return false;
  const code = block.querySelector('code') ?? block;
  const doc = block.ownerDocument;

  // Text from the start of the code element up to the caret — `<br>`-aware, so
  // a newline typed inside the block (materialized as a `<br>`, not a `\n`)
  // still counts as a line boundary. Without this the closing-fence line is
  // never recognized and Enter can't exit the block.
  const range = selection.getRangeAt(0);
  const measure = doc.createRange();
  measure.selectNodeContents(code);
  try {
    measure.setEnd(range.startContainer, range.startOffset);
  } catch {
    return false;
  }
  const beforeText = textWithBreaks(measure.cloneContents());
  const fullText = textWithBreaks(code);
  const afterText = fullText.slice(beforeText.length);

  const lineStart = beforeText.lastIndexOf('\n') + 1;
  const currentLine = beforeText.slice(lineStart);
  // Exit only on a BARE fence (all backticks) at least as long as the block's
  // opening fence. A shorter run is an inner nested fence and stays as literal
  // body text — so pressing Enter after an inner ``` inside a ````-opened
  // block adds a newline instead of closing the outer block.
  const fenceLen = Math.max(
    3,
    Number.parseInt(block.getAttribute('data-fence') ?? '3', 10) || 3,
  );
  const closeMatch = currentLine.trim().match(/^(`+)$/);
  if (closeMatch === null || (closeMatch[1] ?? '').length < fenceLen) {
    return false;
  }

  // Strip the fence line (and the newline that introduced it).
  let head = beforeText.slice(0, lineStart);
  if (head.endsWith('\n')) head = head.slice(0, -1);
  const remaining = head + afterText;

  const p = doc.createElement('p');
  p.appendChild(doc.createElement('br'));
  if (remaining.trim().length === 0) {
    // Nothing left but the fence → replace the whole block with the paragraph.
    block.replaceWith(p);
  } else {
    code.textContent = remaining;
    block.after(p);
  }

  const r = doc.createRange();
  r.setStart(p, 0);
  r.collapse(true);
  const win = doc.defaultView;
  if (win !== null) {
    const sel = win.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
  }
  return true;
}

/**
 * True when the caret sits on the LAST line of the code block it's in — i.e.
 * there's no further line break between the caret and the end of the code
 * body. `<br>`- and `\n`-aware, and ignores a single trailing placeholder
 * newline (the empty-block `<br>` anchor / conventional trailing blank). Used
 * to gate the "↓ / Shift+Enter escapes the block" affordance so those keys
 * only leave the block once the caret is actually at the bottom.
 */
export function isCaretOnLastLineOfCode(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (
    selection === null ||
    selection.rangeCount === 0 ||
    !selection.isCollapsed
  ) {
    return false;
  }
  const block = getBlockContaining(root, selection.anchorNode);
  if (block === null || block.tagName !== 'PRE') return false;
  const code = block.querySelector('code') ?? block;
  const range = selection.getRangeAt(0);
  if (range.startContainer !== code && !code.contains(range.startContainer)) {
    // Caret resolved onto the <pre> itself (e.g. next to the language chip) —
    // treat as the last line so the user is never trapped.
    return true;
  }
  const after = block.ownerDocument.createRange();
  try {
    after.setStart(range.startContainer, range.startOffset);
    after.setEnd(code, code.childNodes.length);
  } catch {
    return false;
  }
  // Drop one trailing newline (placeholder / conventional) before deciding
  // whether any real line remains below the caret.
  const afterText = textWithBreaks(after.cloneContents()).replace(/\n$/, '');
  return !afterText.includes('\n');
}

/**
 * Escape the code block the caret is in by placing the caret in a paragraph
 * immediately AFTER the `<pre>`. Reuses an already-empty following paragraph
 * (so repeated presses don't pile up blank lines) or inserts a fresh
 * `<p><br></p>`. Returns `true` when it moved the caret out; `false` when the
 * caret wasn't in a code block. This is the keyboard exit for ↓ / Shift+Enter.
 */
export function tryExitCodeBlockBelow(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (selection === null || selection.rangeCount === 0) return false;
  const block = getBlockContaining(root, selection.anchorNode);
  if (block === null || block.tagName !== 'PRE') return false;
  const doc = block.ownerDocument;
  const next = block.nextElementSibling as HTMLElement | null;
  let target: HTMLElement;
  if (
    next !== null &&
    next.tagName === 'P' &&
    (next.textContent ?? '').trim().length === 0
  ) {
    target = next;
  } else {
    const p = doc.createElement('p');
    p.appendChild(doc.createElement('br'));
    block.after(p);
    target = p;
  }
  const r = doc.createRange();
  r.setStart(target, 0);
  r.collapse(true);
  const win = doc.defaultView;
  if (win !== null) {
    const sel = win.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
  }
  return true;
}

/**
 * Backspace inside an EMPTY code block: remove the block instead of letting
 * the browser delete the `<code>` and leave a `contenteditable="false"`
 * chip-only `<pre>` zombie — which can't be typed into, leaks its chip label
 * into serialization, and jams the code-block toggle until reload. When a
 * block precedes the code block the caret merges to its end; otherwise the
 * block becomes a fresh empty paragraph. Returns `true` when it handled the
 * Backspace; `false` (non-empty block, or caret not in a code block) so the
 * caller falls back to the browser's default delete.
 */
export function handleBackspaceInCodeBlock(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (
    selection === null ||
    selection.rangeCount === 0 ||
    !selection.isCollapsed
  ) {
    return false;
  }
  const block = getBlockContaining(root, selection.anchorNode);
  if (block === null || block.tagName !== 'PRE') return false;
  const code = block.querySelector('code');
  // `<br>`-aware body text, minus the trailing placeholder newline. A missing
  // `<code>` or a lone `<br>` both read as empty.
  const bodyText =
    code === null ? '' : textWithBreaks(code).replace(/\n$/, '');
  if (bodyText.trim().length !== 0) return false; // non-empty → normal delete

  const doc = block.ownerDocument;
  const win = doc.defaultView;
  const prev = block.previousElementSibling as HTMLElement | null;
  const r = doc.createRange();
  if (prev !== null) {
    block.remove();
    r.selectNodeContents(prev);
    r.collapse(false); // caret at the end of the preceding block
  } else {
    const p = doc.createElement('p');
    p.appendChild(doc.createElement('br'));
    block.replaceWith(p);
    r.setStart(p, 0);
    r.collapse(true);
  }
  const sel = win?.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(r);
  return true;
}

/**
 * Toggle a fenced code block at the selection. Same convention as
 * blockquote — re-pressing returns to a plain paragraph.
 */
export function toggleCodeBlock(
  root: HTMLElement,
  selection: Selection | null,
): void {
  if (selection === null || selection.rangeCount === 0) return;
  const anchor = selection.anchorNode;
  const block = getBlockContaining(root, anchor);
  if (block === null) return;
  if (block.tagName === 'PRE') {
    // Drop the wrapping <pre><code> and replace with a paragraph
    // carrying the same text.
    const doc = block.ownerDocument;
    const p = doc.createElement('p');
    p.textContent = block.textContent ?? '';
    block.replaceWith(p);
    return;
  }
  const doc = block.ownerDocument;
  const pre = doc.createElement('pre');
  const code = doc.createElement('code');
  code.textContent = block.textContent ?? '';
  pre.appendChild(code);
  block.replaceWith(pre);
}

// ---------- Nested list operations (Tab / Shift+Tab) ----------

/**
 * Indent the `<li>` containing the caret by one level. The item
 * becomes the last child of the previous sibling `<li>`'s nested
 * list (creating it if needed). No-op when there's no previous
 * sibling (first item in a list can't indent).
 *
 * Returns `true` when the DOM was modified.
 */
export function indentListItem(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (selection === null || selection.rangeCount === 0) return false;
  const li = getListItemContaining(root, selection.anchorNode);
  if (li === null) return false;
  const parentList = li.parentElement;
  if (
    parentList === null ||
    (parentList.tagName !== 'UL' && parentList.tagName !== 'OL')
  ) {
    return false;
  }
  const prev = li.previousElementSibling as HTMLLIElement | null;
  if (prev === null) return false;
  // Reuse a nested list inside `prev` if one already exists at the
  // tail; otherwise create one of the same kind as `parentList`.
  let nestedList = prev.lastElementChild;
  if (
    nestedList === null ||
    (nestedList.tagName !== 'UL' && nestedList.tagName !== 'OL')
  ) {
    nestedList = prev.ownerDocument.createElement(parentList.tagName);
    prev.appendChild(nestedList);
  }
  nestedList.appendChild(li);
  return true;
}

/**
 * Outdent the `<li>` containing the caret by one level. When the
 * item lives inside a nested list, the item moves to the parent list
 * just after the enclosing `<li>`. When the item is already at the
 * top level of a list, the item is converted to a `<p>` and the
 * surrounding list is split if needed.
 *
 * Returns `true` when the DOM was modified.
 */
export function outdentListItem(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (selection === null || selection.rangeCount === 0) return false;
  const li = getListItemContaining(root, selection.anchorNode);
  if (li === null) return false;
  const parentList = li.parentElement;
  if (
    parentList === null ||
    (parentList.tagName !== 'UL' && parentList.tagName !== 'OL')
  ) {
    return false;
  }
  // Is the parent list nested inside another <li>?
  const parentLi = parentList.parentElement;
  if (
    parentLi !== null &&
    parentLi.tagName === 'LI' &&
    parentLi.parentElement !== null &&
    (parentLi.parentElement.tagName === 'UL' ||
      parentLi.parentElement.tagName === 'OL')
  ) {
    // Split parentList around `li` so siblings after `li` remain
    // nested (they shouldn't outdent with us).
    splitListAround(parentList, li);
    // Move `li` to be the next sibling of `parentLi`.
    parentLi.parentElement.insertBefore(li, parentLi.nextSibling);
    cleanEmptyList(parentList);
    return true;
  }
  // Top-level list: convert the <li> to a paragraph block at the
  // root, splitting the list if needed. Anchor on `parentList.nextSibling`
  // *before* the split runs so we still have a stable insertion
  // point if the split removes `parentList` itself (single-item list).
  const root2 = parentList.parentElement;
  if (root2 === null) return false;
  const doc = root2.ownerDocument;
  const anchor = parentList.nextSibling;
  splitListAround(parentList, li);
  const p = doc.createElement('p');
  const trailingNested: HTMLElement[] = [];
  while (li.firstChild !== null) {
    const child = li.firstChild;
    if (
      child.nodeType === Node.ELEMENT_NODE &&
      ((child as HTMLElement).tagName === 'UL' ||
        (child as HTMLElement).tagName === 'OL')
    ) {
      // Pull nested lists out so they end up as siblings AFTER the
      // new paragraph.
      li.removeChild(child);
      trailingNested.push(child as HTMLElement);
      continue;
    }
    p.appendChild(child);
  }
  if (p.childNodes.length === 0) p.appendChild(doc.createElement('br'));
  // Insert the new paragraph (and any extracted nested lists) at the
  // anchor point — `anchor` may be null (was the last child) which
  // resolves to append-at-end on `insertBefore`.
  root2.insertBefore(p, anchor);
  for (const n of trailingNested) root2.insertBefore(n, anchor);
  // The original li is empty now and was already detached from the
  // list by `splitListAround`. No further cleanup needed.
  return true;
}

/**
 * Split `list` such that `pivot` (an <li> currently inside `list`)
 * becomes the first child of a fresh list of the same kind, and any
 * items AFTER `pivot` migrate to a third list following the pivot.
 * Net effect: `list` now ends BEFORE `pivot`, `pivot` is detached so
 * the caller can place it wherever, and the trailing items live in
 * their own list block adjacent to the original location.
 */
function splitListAround(list: HTMLElement, pivot: HTMLLIElement): void {
  const doc = list.ownerDocument;
  const parent = list.parentNode;
  if (parent === null) return;
  // Move all trailing siblings (after pivot) into a fresh list.
  const trailing = doc.createElement(list.tagName);
  let cur = pivot.nextSibling;
  while (cur !== null) {
    const next = cur.nextSibling;
    trailing.appendChild(cur);
    cur = next;
  }
  parent.insertBefore(trailing, list.nextSibling);
  // Remove pivot from `list` (caller re-attaches).
  list.removeChild(pivot);
  cleanEmptyList(trailing);
  cleanEmptyList(list);
}

function cleanEmptyList(list: HTMLElement): void {
  if (list.children.length === 0) list.parentNode?.removeChild(list);
}

/**
 * Handler for Enter pressed inside a list item. Returns `true` when
 * the keypress was consumed (caller should `preventDefault`).
 *
 * Behavior:
 *   - Empty top-level <li> → convert to a <p> after the list (exits
 *     the list) and remove the empty <li>.
 *   - Empty nested <li> → outdent one level (the next press exits
 *     the list completely).
 *   - Non-empty <li> → fall through (browser default splits the item).
 */
export function handleEnterInList(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (selection === null || selection.rangeCount === 0) return false;
  if (!selection.isCollapsed) return false;
  const li = getListItemContaining(root, selection.anchorNode);
  if (li === null) return false;
  if (!isEmptyListItem(li)) return false;
  const parentList = li.parentElement;
  if (parentList === null) return false;
  const isNested =
    parentList.parentElement !== null &&
    parentList.parentElement.tagName === 'LI';
  if (isNested) {
    outdentListItem(root, selection);
    return true;
  }
  // Top-level empty li → exit list.
  const doc = parentList.ownerDocument;
  const root2 = parentList.parentElement;
  if (root2 === null) return false;
  splitListAround(parentList, li);
  const p = doc.createElement('p');
  p.appendChild(doc.createElement('br'));
  root2.insertBefore(p, parentList.nextSibling);
  // Place caret in the new paragraph.
  const win = root.ownerDocument.defaultView;
  if (win !== null) {
    const range = doc.createRange();
    range.selectNodeContents(p);
    range.collapse(true);
    const sel = win.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }
  return true;
}

/**
 * Handler for Backspace pressed at the very start of a list item.
 * Outdents one level when nested; converts to a paragraph at the top
 * level. Returns `true` when consumed.
 */
export function handleBackspaceInList(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (selection === null || selection.rangeCount === 0) return false;
  if (!selection.isCollapsed) return false;
  const range = selection.getRangeAt(0);
  if (range.startOffset !== 0) return false;
  const li = getListItemContaining(root, selection.anchorNode);
  if (li === null) return false;
  // Verify caret is at the very start of the li's first text run.
  if (!isCaretAtLiStart(li, range)) return false;
  return outdentListItem(root, selection);
}

function isEmptyListItem(li: HTMLLIElement): boolean {
  // An <li> counts as empty when its text content is empty (ignoring
  // a single trailing <br>) and it has no nested list.
  const text = (li.textContent ?? '').replace(/​/g, '').replace(/ /g, '').trim();
  if (text.length > 0) return false;
  const hasNested = Array.from(li.children).some(
    (c) => c.tagName === 'UL' || c.tagName === 'OL',
  );
  return !hasNested;
}

function isCaretAtLiStart(li: HTMLLIElement, range: Range): boolean {
  const probe = li.ownerDocument.createRange();
  probe.selectNodeContents(li);
  probe.collapse(true);
  // Compare positions — caret must be at or before the li's first
  // content character.
  return (
    range.compareBoundaryPoints(Range.START_TO_START, probe) === 0 ||
    range.startOffset === 0
  );
}
